import type { NavSection } from "@skypointcloud/skypoint-ui";
import { getAllNavRoutes, HUB_ROUTES, navIdsOwning } from "../views/dashboard/sidebar/routeMapping";
import {
  isInstanceProductsPath,
  isSharedUsersPath,
  type NavId,
} from "../views/dashboard/sidebar/sidebarConfig";
import { activeProductFor } from "./products/activeProduct";
import { DEFAULT_PRODUCT } from "../../shared/products/keys";
import { ROUTES } from "./shared/constant";

/**
 * What the route guard admits, as plain functions (AppLayout's useRouteGuard calls them).
 *
 * UI ONLY. This decides what the browser shows; it is never the protection. Every hire router and
 * every performance route on the server gates on its own (requireProduct plus a permission).
 */

/** Routes that are always accessible regardless of RBAC permissions */
export const OPEN_ROUTE_PREFIXES = ["/account", "/errorpage", "/callback", "/survey/"];

/** Whether `pathname` is `base` or a page beneath it. */
export function isUnder(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(base + "/");
}

/**
 * Every route base the given sections draw, children included.
 *
 * "/" is kept rather than filtered out. It used to be dropped and then hardcoded as always
 * accessible, with one exception bolted on for the Client role — which is why enabling Dashboard
 * for a Client put the entry in their sidebar and then bounced the click straight back to
 * Applicants (#806). Whether "/" is reachable is a question the nav config already answers.
 */
export function allowedBasesOf(sections: NavSection[]): string[] {
  const bases: string[] = [];
  for (const section of sections) {
    for (const item of section.items) {
      bases.push(...getAllNavRoutes(item.id as NavId));
      for (const child of (item as { children?: { id: string }[] }).children ?? []) {
        bases.push(...getAllNavRoutes(child.id as NavId));
      }
    }
  }
  return bases;
}

/**
 * Offer pages have no sidebar entry, so the allow-list the guard builds from the
 * sidebar cannot admit them. They used to sit in OPEN_ROUTE_PREFIXES, which
 * opened all of them, templates and workflows included, to every role. They
 * answer to Role Management's Offers switches instead, the keys the server
 * gates /hiring/offers on.
 *
 * Returns undefined for a path this does not decide, leaving it to the
 * allow-list like any other page:
 * - Templates and workflows have sidebar entries (admin / instance admin). They
 *   are carved out first because /offers is a prefix of both, and `offers.view`
 *   must not open them.
 * - Approvals is the named approvers' queue. /hiring/my-approvals sits outside
 *   the server's offers gate and returns only the caller's own approval steps,
 *   and an approver is picked from every user in the tenant whatever their
 *   role, so anyone the guard admits at all may open it.
 */
export function offerRouteAccess(
  pathname: string,
  permissions: Record<string, boolean>,
): boolean | undefined {
  if (!isUnder(pathname, ROUTES.OFFERS)) return undefined;
  if (isUnder(pathname, ROUTES.OFFER_TEMPLATES) || isUnder(pathname, ROUTES.OFFER_WORKFLOWS)) {
    return undefined;
  }
  if (isUnder(pathname, ROUTES.OFFER_APPROVALS)) return true;
  return permissions["offers.view"] === true;
}

/**
 * The Users page both products share (spec §5: its gate is "hire users.view or perf_hr", and the
 * performance sidebar links perf_hr to it). Admits it to a caller the guard has established holds
 * performance.users.manage on an instance with the performance product; otherwise returns
 * undefined and the hire rule decides exactly as before. Never refuses, never touches another page.
 */
export function sharedUsersRouteAccess(
  pathname: string,
  managesProductUsers: boolean,
): true | undefined {
  return managesProductUsers && isSharedUsersPath(pathname) ? true : undefined;
}

/**
 * A tiled page (2026-10-08 shell spec, D5: Settings; and Hire's Templates, HUB_ROUTES): admitted,
 * exactly and nothing beneath it, to whoever the sidebar draws its item for. Returns undefined for
 * any other path, or when the item is not drawn, leaving it to the rules below exactly as before.
 * Never refuses. The pages its tiles open are each admitted by their own entry, as they always were.
 */
export function settingsHubRouteAccess(pathname: string, sections: NavSection[]): true | undefined {
  const drawn = drawnIdsOf(sections);
  const admitted = Object.entries(HUB_ROUTES).some(
    ([navId, route]) => route === pathname && drawn.has(navId),
  );
  return admitted ? true : undefined;
}

/**
 * A hire page on an instance without the hire product (performance-only, once products resolved;
 * Task 17 ruling 5, the client half of Task 13). Refused, so a typed hire URL never renders there,
 * except:
 *  - the landing `/`, admitted: HomeRoute decides where a performance-only instance goes
 *    (performance Home, the shared Users page, or NO_PRODUCT_ACCESS) and never picks hire;
 *  - the shared Users page (spec §5, Task 12.b), left to the rules below exactly as before;
 *  - the staff Products page (Task 18.b), product-neutral: left to the allow-list, which admits it
 *    only where the sidebar draws it (a platform admin).
 * Returns undefined, deciding nothing, whenever the instance has hire.
 */
export function hireOffRouteAccess(
  pathname: string,
  instanceHasHire: boolean,
): boolean | undefined {
  if (instanceHasHire) return undefined;
  if (pathname === "/") return true;
  if (isSharedUsersPath(pathname) || isInstanceProductsPath(pathname)) return undefined;
  return false;
}

/** Every nav id the sections draw, children included. */
function drawnIdsOf(sections: NavSection[]): Set<string> {
  const ids = new Set<string>();
  for (const section of sections) {
    for (const item of section.items) {
      ids.add(item.id);
      for (const child of (item as { children?: { id: string }[] }).children ?? []) {
        ids.add(child.id);
      }
    }
  }
  return ids;
}

/**
 * A non-default product's route (today `/performance/*`), failing closed.
 *
 * The product's landing (`/performance`) is a prefix of every route it has, so the prefix rule hire
 * uses would let Home open Settings and every Phase 3 placeholder. Instead the path belongs to its
 * most specific route pattern (the same longest match the sidebar highlights with), and is admitted
 * only when the sidebar draws that page. Sections without it (the product not enabled, its context
 * not loaded, a role without the switch) admit nothing here, and the guard redirects.
 */
function productRouteAllowed(pathname: string, sections: NavSection[]): boolean {
  // Every item tied for the most specific pattern owns the page (E2 O-1: the file pages belong to
  // My file and the Org chart); any one of them drawn admits it. A shorter pattern never does.
  const drawn = drawnIdsOf(sections);
  return navIdsOwning(pathname).some((owner) => drawn.has(owner));
}

/**
 * Where a REFUSED performance address sends the caller instead of their first item (#1435/#1437,
 * operator ruling 2026-10-20). An employee draws no Evaluations item, so:
 *  - one evaluation's address (`/performance/evaluations/:id`, the link a manager's view uses)
 *    opens as their own (`/performance/evaluations/my/:id`, under Home). The server still answers
 *    only their own, once complete; the page sends one in progress to its self-evaluation;
 *  - the list (`/performance/evaluations`, any tab) opens My file's Evaluations tab, where their
 *    own evaluations are listed.
 * And (approved design 2026-10-23) a manager and HR draw no Document Centre item: its address
 * (`/performance/documents`) opens Records' Documents tab, where the same list now lives.
 * Returns undefined for any other path, or where the target is refused too, leaving the guard's
 * usual fallback. The query (the instance) is kept. UI only, like every rule here.
 */
export function refusedRouteRedirect(
  pathname: string,
  search: string,
  sections: NavSection[],
): string | undefined {
  const path = pathname.replace(/\/+$/, "");
  const list = ROUTES.PERFORMANCE_EVALUATIONS;
  let target: string | undefined;
  let tab: string | undefined;
  if (path === ROUTES.PERFORMANCE_DOCUMENTS) {
    target = ROUTES.PERFORMANCE_RECORDS_TAB.replace(":tab", "documents");
  } else if (path === list || path.startsWith(`${list}/t/`)) {
    target = ROUTES.PERFORMANCE_FILE;
    tab = "evaluations";
  } else {
    const one = new RegExp(`^${list}/([^/]+)$`).exec(path);
    if (one && one[1] !== "my" && one[1] !== "t")
      target = ROUTES.PERFORMANCE_EVALUATION_MY.replace(":evaluationId", one[1]!);
  }
  if (!target || !productRouteAllowed(target, sections)) return undefined;
  const query = new URLSearchParams(search);
  if (tab) query.set("tab", tab);
  const qs = query.toString();
  return qs ? `${target}?${qs}` : target;
}

export function routeAllowed(
  pathname: string,
  sections: NavSection[],
  permissions: Record<string, boolean>,
  /**
   * The caller holds performance.users.manage on an instance with the performance product. Opens
   * the shared Users page and nothing else; absent or false changes nothing.
   */
  managesProductUsers = false,
  /**
   * The instance has the hire product (or products have not resolved yet, or a legacy empty list).
   * False only on a resolved performance-only instance; see hireOffRouteAccess.
   */
  instanceHasHire = true,
): boolean {
  if (OPEN_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return true;
  if (activeProductFor(pathname) !== DEFAULT_PRODUCT) {
    return productRouteAllowed(pathname, sections);
  }
  const hireOff = hireOffRouteAccess(pathname, instanceHasHire);
  if (hireOff !== undefined) return hireOff;
  // Hire routes: exactly the rule AppLayout has always applied.
  return (
    offerRouteAccess(pathname, permissions) ??
    sharedUsersRouteAccess(pathname, managesProductUsers) ??
    settingsHubRouteAccess(pathname, sections) ??
    allowedBasesOf(sections).some((base) =>
      // "/" is a prefix of every path, so it may only ever match exactly —
      // otherwise holding the Dashboard entry would authorise the whole app.
      base === "/" ? pathname === "/" : isUnder(pathname, base),
    )
  );
}
